import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Grid,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  LinearProgress,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Paper,
} from "@mui/material";
import { Delete, Add, Remove } from "@mui/icons-material";
import Layout from "../../components/layout";
import userService from "../../services/UserService";
import SalonService from "../../services/salonServices";
import { DecryptData, EncryptData, jsonParser } from "../../helpers/helper";
import ValidationModal from "../BootstrapValidationModal";
import bookingService from "../../services/booking";
import InputLabel from "@mui/material/InputLabel";
import { useHistory } from "react-router-dom";
import RejectionModal from "../RejectionModal";
import { printReceipts } from "../../components/print";

const AddBooking = () => {
  const history = useHistory();

  const [users, setUsers] = useState<any[]>([]);
  const [optionData, setOptionData] = useState<any[]>([]);
  const [cart, setCart] = useState<any[]>([]);
  const [isNewUser, setIsNewUser] = useState(false);
  const [newUser, setNewUser] = useState({
    full_name: "",
    email: "",
    phone_no: "",
    address: "",
  });

  const [discountType, setDiscountType] = useState("fixed");
  const [discountValue, setDiscountValue] = useState(0);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [payments, setPayments] = useState({ cash: 0, online: 0, pos: 0 });
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");

  const [openVarModal, setOpenVarModal] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [tempSelectedOptions, setTempSelectedOptions] = useState<any>({});
  const [searchKey, setSearchKey] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const [isSubmittingModalOpen, setIsSubmittingModalOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = React.useState(0);
  const [isReciptOpen, setIsReciptOpen] = useState(false);
  const [disablePrint, setDisablePrint] = useState(false);
  const [cashierName, setCashierName] = useState("");
  const [userId, setUserId] = React.useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    let intervalId;
    let isMounted = true;

    const startProgress = async () => {
      intervalId = await setInterval(() => {
        setProgress((oldProgress) => {
          const newProgress = oldProgress + 100 / steps;

          if (newProgress >= 100) {
            clearInterval(intervalId);
            setTimeout(() => {
              if (isMounted) {
                setIsSubmittingModalOpen(false);
                history.push(`/booking`);
              }
            }, 500);

            return 100;
          }

          return newProgress;
        });
      }, intervalDuration);
      await setIsSubmitting(false);
    };

    const intervalDuration = 30;
    const totalTime = 1000;
    const steps = totalTime / intervalDuration;

    if (isSubmittingModalOpen) {
      startProgress();
    }

    return () => {
      isMounted = false;

      clearInterval(intervalId);
    };
  }, [isSubmittingModalOpen]);

  const closeSubmittingModal = () => {
    setIsSubmittingModalOpen(false);
  };

  useEffect(() => {
    const init = async () => {
      await fetchUsers();
      await fetchAllServices();
    };
    init();
  }, []);

  useEffect(() => {
    const getData = async () => {
      const data = localStorage.getItem("user");
      const parsedData = JSON.parse(data);
      const name = DecryptData(parsedData.full_name);
      const userId = parsedData.id;
      setCashierName(name);
      setUserId(userId);
    };

    getData();
  });

  const fetchUsers = async () => {
    const data = await userService.findCustomers();
    const decryptedData = await DecryptData(data);
    const parsedData = jsonParser(decryptedData);

    // Helper to format names nicely (e.g., "ali ahmad" -> "Ali Ahmad")
    const formatName = (name: string) => {
      if (!name) return "";
      return name
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const resolvedData = await Promise.all(
      parsedData.map(async (user: any) => ({
        ...user,
        full_name: user.full_name
          ? formatName(await DecryptData(user.full_name))
          : "",
        phone_no: user.phone_no ? await DecryptData(user.phone_no) : "",
      }))
    );

    setUsers(resolvedData);
  };

  const fetchAllServices = async () => {
    const data = await SalonService.findAllWithPackage();
    const decryptedData = await DecryptData(data.serviceData);
    const parsedData = jsonParser(decryptedData);
    // Helper to format names (capitalizes first letter of each word, lowercase rest)
    const formatName = (name: string) => {
      if (!name) return "";
      return name
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const transformed = await Promise.all(
      parsedData.map(async (service: any) => ({
        ...service,
        serviceName: service.serviceName
          ? formatName(await DecryptData(service.serviceName))
          : "",
        price: service.price ? await DecryptData(service.price) : "0",
        variations:
          service.variations?.length > 0
            ? await Promise.all(
                service.variations.map(async (v: any) => ({
                  ...v,
                  typeName: v.typeName
                    ? formatName(await DecryptData(v.typeName))
                    : "",
                  options: await Promise.all(
                    v.options.map(async (opt: any) => ({
                      ...opt,
                      name: opt.name
                        ? formatName(await DecryptData(opt.name))
                        : "",
                      price: await DecryptData(opt.price),
                    }))
                  ),
                }))
              )
            : [],
      }))
    );
    setOptionData(transformed);
  };

  const handleAddToCart = (item: any) => {
    setCart((prev) => {
      const exists = prev.find((i) => i.cartId === item.cartId);
      if (exists) {
        return prev.map((i) =>
          i.cartId === item.cartId ? { ...i, qty: i.qty + 1 } : i
        );
      }
      return [...prev, { ...item, qty: 1 }];
    });
  };

  const updateQty = (cartId: string, delta: number) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.cartId === cartId) {
          const newQty = item.qty + delta;
          return { ...item, qty: newQty > 0 ? newQty : 1 };
        }
        return item;
      })
    );
  };

  const handleConfirmVariations = () => {
    const optionsArray = Object.values(tempSelectedOptions) as any[];
    const newCartItems: any[] = [];
    const linkedVariations = selectedService.variations.filter(
      (v: any) => v.uniqueNumber !== null
    );
    const linkedOptions = optionsArray.filter((opt) =>
      linkedVariations.some((lv) =>
        lv.options.some((o: any) => o.id === opt.id)
      )
    );

    if (linkedOptions.length > 0) {
      const highestPrice = Math.max(
        ...linkedOptions.map((o) => parseFloat(o.price || 0))
      );
      const variationLabels = linkedOptions.map((o) => o.name).join(", ");
      const combinationKey = linkedOptions
        .map((o) => o.id)
        .sort((a, b) => a - b)
        .join(",");
      newCartItems.push({
        cartId: `${combinationKey}`,
        id: selectedService.id,
        name: `${selectedService.serviceName} (${variationLabels})`,
        price: highestPrice,
        type: "variation",
      });
    }

    const independentOptions = optionsArray.filter(
      (opt) =>
        !linkedVariations.some((lv) =>
          lv.options.some((o: any) => o.id === opt.id)
        )
    );
    independentOptions.forEach((opt) => {
      newCartItems.push({
        cartId: `${opt.id}`,
        id: selectedService.id,
        name: `${selectedService.serviceName} - ${opt.name}`,
        price: parseFloat(opt.price || 0),
        type: "independent_variation",
      });
    });

    newCartItems.forEach((item) => handleAddToCart(item));
    setOpenVarModal(false);
    setTempSelectedOptions({});
  };

  // --- MATH LOGIC (As per your explanation) ---
  const totalInclusive = cart.reduce(
    (acc, curr) => acc + curr.price * curr.qty,
    0
  );

  // Step 1: Find the original Base Price (Total / 1.08)
  const totalBasePrice = totalInclusive / 1.08;

  // Step 2: Calculate Discount (Applied to Base)
  const discountAmt =
    discountType === "percent"
      ? (totalBasePrice * discountValue) / 100
      : discountValue;

  const discountInPercent =
    discountType === "fixed"
      ? totalBasePrice > 0
        ? Number(((discountValue / totalBasePrice) * 100).toFixed(2))
        : 0
      : Number(Number(discountValue).toFixed(2));

  // Step 3: Net Base Price (Base - Discount)
  const netBasePrice = Math.max(0, totalBasePrice - discountAmt);

  // Step 4: Calculate 8% Tax on Net Base
  const finalTax = netBasePrice * 0.08;

  // Step 5: Final Subtotal
  const grandTotal = netBasePrice + finalTax;

  const totalPaid = payments.cash + payments.online + payments.pos;

  const pendingRaw = grandTotal - totalPaid;
  const pending = Math.abs(pendingRaw) < 0.000001 ? 0 : pendingRaw;

  const filteredUsers = users.filter((u) => {
    return `${u.id} ${u.full_name} ${u.phone_no}`
      .toLowerCase()
      .includes(searchText.toLowerCase());
  });

  // --- Check Duplicate Phone Handler ---
  const checkDuplicatePhone = (phone: string) => {
    if (!phone || phone.trim() === "") return null;

    // Helper to normalize phone: remove spaces/hyphens and replace leading '03' with '92'
    const normalizePhone = (num: string) => {
      let cleaned = num.replace(/[\s-]/g, "").trim();
      if (cleaned.startsWith("03")) {
        cleaned = "92" + cleaned.slice(1);
      }
      return cleaned;
    };

    const inputNormalized = normalizePhone(phone);

    return users.find((u) => {
      if (!u.phone_no) return false;
      const userNormalized = normalizePhone(u.phone_no);
      return userNormalized === inputNormalized;
    });
  };
  const existingPhoneMatch = isNewUser
    ? checkDuplicatePhone(newUser.phone_no)
    : null;

  // Validation function for submit button click
  const validateBooking = () => {
    // 1. Customer validation
    if (!isNewUser) {
      // existing user must be selected
      if (!selectedUser) {
        alert("Please select a customer");
        return false;
      }
    } else {
      // new user name required
      if (!newUser.full_name?.trim()) {
        alert("Full name is required for new user");
        return false;
      }
      // Check if new user's phone number already exists
      // if (newUser.phone_no?.trim()) {
      //   const existingUserMatch = checkDuplicatePhone(newUser.phone_no);
      //   if (existingUserMatch) {
      //     alert(
      //       `Phone number already exists with that name (${existingUserMatch.full_name}). Please select that user in existing or else change the number.`
      //     );
      //     return false;
      //   }
      // }
    }

    // 2. Services
    if (cart.length === 0) {
      alert("Please select at least one service");
      return false;
    }

    // 3. Discount + reason
    if (discountValue > 0 && !reason.trim()) {
      alert("Please provide reason for discount");
      return false;
    }

    if (grandTotal > 0) {
      // 4. Payment method
      if (paymentMethods.length === 0) {
        alert("Please select at least one payment method");
        return false;
      }

      // 5. Payment amount validation
      const totalPayment =
        (payments.cash || 0) + (payments.online || 0) + (payments.pos || 0);

      if (totalPayment <= 0) {
        alert("Payment amount must be greater than 0");
        return false;
      }
    }

    return true;
  };

  const onSubmit = async (record) => {
    setIsSubmitting(true);
    try {
      let userData = {};

      const data = localStorage.getItem("user");
      const parsedData = JSON.parse(data);
      const userId = parsedData.id;

      const actualGrandTotal = totalBasePrice + finalTax - discountAmt;
      const isFree = actualGrandTotal <= 0;

      const baseBookingPayload = {
        createdById: userId,
        bookingDate: new Date(),
        subtotal: Math.round(totalBasePrice).toString(),
        discount: discountAmt,
        tax: Math.round(finalTax).toString(),
        grandTotal: Math.floor(actualGrandTotal).toString(),
        comments: description,
        isExistingCustomer: !isNewUser,
        services: cart,
        reason: reason,
        amountPaid: payments.cash + payments.online + payments.pos,
        pendingAmount: pending,
        paymentMethods: isFree ? [] : paymentMethods,
        payments: isFree ? { cash: 0, online: 0, pos: 0 } : payments,
        selectedUser,
        isAdvanceBooking: false,
      };

      if (isNewUser) {
        userData = {
          full_name: EncryptData(newUser.full_name),
          ...(newUser.phone_no && { phone_no: EncryptData(newUser.phone_no) }),
          ...(newUser.email && { email: newUser.email }),
          createdById: userId,
          address: EncryptData(newUser.address),
        };
      }

      const createBillingDto = {
        userDto: userData,
        baseBookingPayload,
      };

      await bookingService.save(createBillingDto);
      setIsSubmittingModalOpen(true);
    } catch (err) {
      console.error("Error saving data:", err.message);
      setIsSubmitting(false);
    }
  };

  const closeModalRecipt = () => {
    setIsReciptOpen(false);
  };

  const onPrint = async () => {
    setDisablePrint(true);
    setIsSubmitting(true);

    try {
      let userData = {};

      const data = localStorage.getItem("user");
      const parsedData = JSON.parse(data);
      const userId = parsedData.id;

      const actualGrandTotal = totalBasePrice + finalTax - discountAmt;
      const baseBookingPayload = {
        createdById: userId,
        bookingDate: new Date(),
        subtotal: Math.round(totalBasePrice).toString(),
        discount: discountAmt,
        tax: Math.round(finalTax).toString(),
        grandTotal: Math.floor(actualGrandTotal).toString(),
        comments: description,
        isExistingCustomer: !isNewUser,
        services: cart,
        reason: reason,
        amountPaid: payments.cash + payments.online + payments.pos,
        pendingAmount: pending,
        paymentMethods: paymentMethods,
        payments: payments,
        selectedUser,
        isAdvanceBooking: false,
      };

      if (isNewUser) {
        userData = {
          full_name: EncryptData(newUser.full_name),
          ...(newUser.phone_no && { phone_no: EncryptData(newUser.phone_no) }),
          ...(newUser.email && { email: newUser.email }),
          createdById: userId,
          address: EncryptData(newUser.address),
        };
      }

      const createBillingDto = {
        userDto: userData,
        baseBookingPayload,
      };

      const newBooking = await bookingService.save(createBillingDto);
      // ------------------------------------------------------------------- //
      // print receipt ------------------------------------------------
      if (newBooking.data.success) {
        try {
          const paymentMethodsString = paymentMethods.join(", ");

          let paymentCode = "";
          const method = paymentMethodsString?.toLowerCase() || "";

          if (method === "cash") {
            paymentCode = "C";
          } else if (
            method === "online" ||
            method === "pos" ||
            method === "online,pos" ||
            method === "pos,online"
          ) {
            paymentCode = "D";
          } else {
            // If it's a mix like "cash,online" or "cash,pos"
            paymentCode = "CD";
          }

          const dateObj = new Date();
          const day = String(dateObj.getDate()).padStart(2, "0");
          const month = String(dateObj.getMonth() + 1).padStart(2, "0");
          const year = dateObj.getFullYear();

          // 3. Construct the Custom Receipt ID
          // Format: BO - [MM][DD][YY] - [CODE] - [ID]
          // Note: Using 26 for year as per your example '031026'
          const shortYear = String(year).slice(-2);
          const customReceiptId = `BO-M-${month}${day}${shortYear}-${paymentCode}-${newBooking.data.bookingId}`;

          const discountedBasePrice = totalBasePrice - discountAmt;
          const actualGrandTotal = discountedBasePrice + finalTax;
          const subtotalBeforeTax = totalInclusive - discountAmt;
          const totalPaid = payments.cash + payments.online + payments.pos;

          const payload: any = {
            totalExcTax: subtotalBeforeTax,
            customerName: isNewUser
              ? newUser.full_name
              : selectedUser.full_name,
            cashierName: cashierName,
            services: cart,
            reason: reason,
            subtotal: subtotalBeforeTax,
            praTax: finalTax,
            totalPrice: actualGrandTotal,
            GrandTotal: actualGrandTotal,
            discountInPercentage: discountInPercent,
            discountInAmount: discountAmt,
            amountPaid: totalPaid,
            pending: actualGrandTotal - totalPaid, // Corrected logic
            customReceiptId,
          };

          printReceipts(payload);
          setIsSubmittingModalOpen(true);
        } catch (err) {
          console.error("Error saving data:", err);

          setDisablePrint(false);

          setIsReciptOpen(false);

          setIsSubmitting(false);
        }
      }
    } catch (err) {
      console.error("Error saving data:", err.message);
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4} maxWidth="1400px" sx={{ margin: "0 auto" }}>
          <Grid item xs={12} lg={8}>
            {/* Customer Information */}
            <Box
              className="glass-card"
              sx={{
                mb: 3,
                p: 3,
                borderRadius: 4,
                bgcolor: "#fff",
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#719879" }}
                >
                  Customer Information
                </Typography>
                <Button
                  style={{ color: "#719879" }}
                  onClick={() => setIsNewUser(!isNewUser)}
                >
                  {isNewUser ? "Search Existing" : "+ New"}
                </Button>
              </Box>

              {isNewUser ? (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        size="small"
                        value={newUser.full_name}
                        onChange={(e) =>
                          setNewUser({ ...newUser, full_name: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        size="small"
                        value={newUser.email}
                        onChange={(e) =>
                          setNewUser({ ...newUser, email: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        size="small"
                        value={newUser.phone_no}
                        onChange={(e) =>
                          setNewUser({ ...newUser, phone_no: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Address"
                        size="small"
                        value={newUser.address}
                        onChange={(e) =>
                          setNewUser({ ...newUser, address: e.target.value })
                        }
                      />
                    </Grid>
                  </Grid>

                  {/* Warning and full details display if phone already exists (Non-blocking) */}
                  {existingPhoneMatch && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "rgba(255, 152, 0, 0.1)",
                        border: "1px solid #ff9800",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "#e65100", mb: 1 }}
                      >
                        ⚠️ Phone already exists with name:{" "}
                        {existingPhoneMatch.full_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        <strong>uId:</strong> {existingPhoneMatch.id} |{" "}
                        <strong>Email:</strong>{" "}
                        {existingPhoneMatch.email || "--"} |{" "}
                        <strong>Address:</strong>{" "}
                        {existingPhoneMatch.address || "--"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#666", mt: 0.5, display: "block" }}
                      >
                        Note: You can still proceed with this booking as
                        multiple clients can share the same phone number.
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by uId, name, or phone no..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => {
                      // Delay hiding table briefly so clicking table rows works
                      setTimeout(() => setIsSearchFocused(false), 200);
                    }}
                    sx={{ mb: 2 }}
                  />

                  {selectedUser && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "#f1f8f3",
                        border: "1px solid #719879",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#2d3436" }}
                      >
                        Selected: uID({selectedUser.id}) Name(
                        {selectedUser.full_name}) Phone No(
                        {selectedUser.phone_no || "No Phone"})
                      </Typography>
                      <Chip
                        label="Selected"
                        size="small"
                        sx={{
                          bgcolor: "#719879",
                          color: "white",
                          fontWeight: 700,
                        }}
                        onDelete={() => setSelectedUser(null)}
                      />
                    </Box>
                  )}

                  {/* Table view displays only when search box is focused */}
                  {isSearchFocused && (
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{
                        maxHeight: 220,
                        overflowY: "auto",
                        borderRadius: 2,
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: 700, bgcolor: "#f5f5f5" }}
                            >
                              uId
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 700, bgcolor: "#f5f5f5" }}
                            >
                              Name
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 700, bgcolor: "#f5f5f5" }}
                            >
                              Phone No
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 700, bgcolor: "#f5f5f5" }}
                            >
                              Email
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredUsers.length > 0 ? (
                            filteredUsers.map((u) => {
                              const isSelected = selectedUser?.id === u.id;
                              return (
                                <TableRow
                                  hover
                                  key={u.id}
                                  onMouseDown={() => setSelectedUser(u)} // Using onMouseDown to trigger before blur
                                  sx={{
                                    cursor: "pointer",
                                    bgcolor: isSelected
                                      ? "rgba(113, 152, 121, 0.15) !important"
                                      : "inherit",
                                  }}
                                >
                                  <TableCell>{u.id}</TableCell>
                                  <TableCell
                                    sx={{ fontWeight: isSelected ? 700 : 400 }}
                                  >
                                    {u.full_name}
                                  </TableCell>
                                  <TableCell>{u.phone_no || "--"}</TableCell>
                                  <TableCell>{u.email || "--"}</TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={4}
                                align="center"
                                sx={{ py: 3, color: "text.secondary" }}
                              >
                                No user found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </Box>

            {/* Service Search & Cart */}
            <Box
              className="glass-card"
              sx={{
                p: 3,
                borderRadius: 4,
                bgcolor: "#fff",
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 700, color: "#719879" }}
              >
                Select Services
              </Typography>
              <Autocomplete
                key={searchKey}
                value={null}
                blurOnSelect
                options={optionData}
                getOptionLabel={(o) =>
                  o.variations && o.variations.length > 0
                    ? o.serviceName
                    : `${o.serviceName} - Rs.${o.price}`
                }
                onChange={(_, val) => {
                  if (!val) return;
                  setSearchKey((prev) => prev + 1);
                  if (val.variations.length > 0) {
                    setSelectedService(val);
                    setOpenVarModal(true);
                  } else {
                    handleAddToCart({
                      cartId: `${val.id}`,
                      id: val.id,
                      name: val.serviceName,
                      price: parseFloat(val.price),
                      type: "independent",
                    });
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search service..." />
                )}
              />

              <Box sx={{ mt: 4 }}>
                {cart.map((item) => {
                  const itemTotal = item.price * item.qty;
                  const itemBase = itemTotal / 1.08;
                  const itemTax = itemTotal - itemBase;

                  return (
                    <Box
                      key={item.cartId}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        mb: 1,
                        border: "1px solid #eee",
                        borderRadius: 2,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>
                          {item.name}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Typography variant="caption" color="textSecondary">
                            Base: Rs. {itemBase.toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Tax (8%): Rs. {itemTax.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => updateQty(item.cartId, -1)}
                        >
                          <Remove />
                        </IconButton>
                        <Typography sx={{ fontWeight: 700 }}>
                          {item.qty}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => updateQty(item.cartId, 1)}
                        >
                          <Add />
                        </IconButton>
                        <Typography
                          sx={{
                            minWidth: 90,
                            textAlign: "right",
                            fontWeight: 700,
                          }}
                        >
                          Rs. {itemTotal}
                        </Typography>
                        <IconButton
                          color="error"
                          onClick={() =>
                            setCart(
                              cart.filter((i) => i.cartId !== item.cartId)
                            )
                          }
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "1vw",
                }}
              >
                <div className="singleInputField singleInputFieldTextArea">
                  <InputLabel
                    className="formInputLabels"
                    shrink
                    htmlFor="bootstrap-input"
                  >
                    Comments (If any)
                  </InputLabel>
                  <FormControl variant="standard">
                    <textarea
                      className={`leaveDurationTextArea leaveDurationInput`}
                      onChange={(e: any) => setDescription(e.target.value)}
                      value={description}
                      placeholder="Enter comments here..."
                      rows={6}
                      cols={50}
                    />
                  </FormControl>
                </div>
              </div>
            </Box>
          </Grid>

          {/* Billing Sidebar Redesigned */}
          <Grid item xs={12} lg={4}>
            <Box
              sx={{
                background: "linear-gradient(145deg, #2d3436, #000000)",
                color: "white",
                borderRadius: "24px",
                p: 3,
                position: "sticky",
                top: 20,
              }}
            >
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 800 }}>
                Billing Summary
              </Typography>

              {/* Step 1: Base Price */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  opacity: 0.7,
                }}
              >
                <Typography className="cartItems" variant="body2">
                  Total Base Price
                </Typography>
                <Typography className="cartItems" variant="body2">
                  Rs. {totalBasePrice.toFixed(2)}
                </Typography>
              </Box>

              {/* Step 2: Discount Display (Scenario 2) */}
              {discountValue > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    color: "#ff7675",
                  }}
                >
                  <Typography className="cartItems" variant="body2">
                    Total Discount
                  </Typography>
                  <Typography className="cartItems" variant="body2">
                    - Rs. {discountAmt.toFixed(2)}
                  </Typography>
                </Box>
              )}

              {/* Step 3: Tax Display */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  opacity: 0.7,
                }}
              >
                <Typography className="cartItems" variant="body2">
                  Total Tax (8%)
                </Typography>
                <Typography className="cartItems" variant="body2">
                  Rs. {finalTax.toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.1)" }} />

              {/* Discount Input */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: "rgba(255,255,255,0.05)",
                  borderRadius: "16px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#719879",
                    fontWeight: 700,
                    display: "block",
                    mb: 1,
                  }}
                >
                  Discount
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Select
                    size="small"
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value)}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.05)",
                      color: "white",

                      "& .MuiSelect-icon": {
                        color: "white",
                      },
                    }}
                  >
                    <MenuItem value="fixed">Rs</MenuItem>
                    <MenuItem value="percent">%</MenuItem>
                  </Select>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={discountValue || ""}
                    onChange={(e) => setDiscountValue(Number(e.target.value))}
                    sx={{
                      input: { color: "white", textAlign: "right" },
                      bgcolor: "rgba(255,255,255,0.1)",
                      borderRadius: "8px",
                    }}
                  />
                </Box>
                {/* 2. CONDITIONAL DISCOUNT REASON FIELD */}
                {discountValue > 0 && (
                  <Box sx={{ display: "flex", gap: 1, marginTop: "10px" }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Reason for discount"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      sx={{
                        textarea: { color: "white" },
                        bgcolor: "rgba(255,255,255,0.05)",
                        borderRadius: "8px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: reason
                            ? "rgba(113, 152, 121, 0.5)"
                            : "rgba(255, 118, 117, 0.5)",
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
              >
                <Typography variant="h6">Sub Total</Typography>
                <Typography variant="h6">
                  {" "}
                  Rs. {grandTotal.toFixed(2)}
                </Typography>
              </Box>

              {/* Payment Select & Inputs (Hidden if Grand Total is 0 / 100% Discount) */}
              {grandTotal > 0 && (
                <>
                  {/* Payment Select */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Select
                      multiple
                      displayEmpty
                      value={paymentMethods}
                      onChange={(e: any) => setPaymentMethods(e.target.value)}
                      renderValue={(s) =>
                        s.length === 0 ? "Select Payment" : s.join(", ")
                      }
                      sx={{
                        bgcolor: "rgba(255,255,255,0.05)",
                        color: "white",

                        "& .MuiSelect-icon": {
                          color: "white",
                        },
                      }}
                    >
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="online">Online</MenuItem>
                      <MenuItem value="pos">POS</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Dynamic Payment Inputs */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    {paymentMethods.includes("cash") && (
                      <TextField
                        className="paymentFields"
                        label="Cash Amount"
                        type="number"
                        value={payments.cash || ""}
                        onChange={(e) =>
                          setPayments({
                            ...payments,
                            cash: Number(e.target.value),
                          })
                        }
                        sx={{
                          bgcolor: "rgba(255,255,255,0.05)",
                          borderRadius: "8px",

                          "& .MuiInputBase-input": {
                            color: "white",
                          },

                          "& .MuiInputLabel-root": {
                            color: "white",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#719879",
                          },

                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "black",
                          },
                          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#719879",
                            },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#719879",
                            },
                        }}
                      />
                    )}

                    {paymentMethods.includes("online") && (
                      <TextField
                        className="paymentFields"
                        label="Online Amount"
                        type="number"
                        value={payments.online || ""}
                        onChange={(e) =>
                          setPayments({
                            ...payments,
                            online: Number(e.target.value),
                          })
                        }
                        sx={{
                          bgcolor: "rgba(255,255,255,0.05)",
                          borderRadius: "8px",

                          "& .MuiInputBase-input": {
                            color: "white",
                          },

                          "& .MuiInputLabel-root": {
                            color: "white",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#719879",
                          },

                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "black",
                          },
                          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#719879",
                            },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#719879",
                            },
                        }}
                      />
                    )}

                    {paymentMethods.includes("pos") && (
                      <TextField
                        className="paymentFields"
                        label="POS Amount"
                        type="number"
                        value={payments.pos || ""}
                        onChange={(e) =>
                          setPayments({
                            ...payments,
                            pos: Number(e.target.value),
                          })
                        }
                        sx={{
                          bgcolor: "rgba(255,255,255,0.05)",
                          borderRadius: "8px",

                          "& .MuiInputBase-input": {
                            color: "white",
                          },

                          "& .MuiInputLabel-root": {
                            color: "white",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#719879",
                          },

                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "black",
                          },
                          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#719879",
                            },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#719879",
                            },
                        }}
                      />
                    )}
                  </Box>
                </>
              )}

              {/* Remaining Badge */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "16px",
                  display: "flex",
                  justifyContent: "space-between",
                  bgcolor:
                    pending > 0
                      ? "rgba(255, 118, 117, 0.1)"
                      : "rgba(178, 216, 186, 0.1)",
                  border: `1px solid ${pending > 0 ? "#ff7675" : "#b2d8ba"}`,
                  mb: 3,
                }}
              >
                <Typography>Remaining</Typography>
                <Typography sx={{ fontWeight: 800 }}>
                  Rs. {pending.toFixed(2)}
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                onClick={() => {
                  if (!validateBooking()) return;

                  setIsSubmitModalOpen(true);
                }}
                sx={{
                  bgcolor: "#719879",
                  py: 2,
                  borderRadius: "12px",
                  fontWeight: 700,
                }}
              >
                Complete Booking
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Variation Modal */}
      <Dialog
        open={openVarModal}
        onClose={() => setOpenVarModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Variations</DialogTitle>
        <DialogContent dividers>
          {selectedService?.variations.map((v: any) => {
            const isLinked = v.uniqueNumber !== null;
            return (
              <Box
                key={v.id}
                sx={{
                  mb: 3,
                  p: isLinked ? 2 : 0,
                  bgcolor: isLinked ? "#f9fbf9" : "transparent",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#719879", mb: 1, fontWeight: "bold" }}
                >
                  {v.typeName}{" "}
                  {isLinked ? (
                    <Chip label="Linked" size="small" />
                  ) : (
                    "(Independent)"
                  )}
                </Typography>
                <Grid container spacing={1}>
                  {v.options.map((opt: any) => {
                    const isSelected = isLinked
                      ? tempSelectedOptions[v.id]?.id === opt.id
                      : tempSelectedOptions[`${v.id}-${opt.id}`];
                    return (
                      <Grid item xs={6} key={opt.id}>
                        <Button
                          fullWidth
                          variant={isSelected ? "contained" : "outlined"}
                          onClick={() => {
                            if (isLinked)
                              setTempSelectedOptions({
                                ...tempSelectedOptions,
                                [v.id]: opt,
                              });
                            else {
                              const newT = { ...tempSelectedOptions };
                              if (isSelected) delete newT[`${v.id}-${opt.id}`];
                              else newT[`${v.id}-${opt.id}`] = opt;
                              setTempSelectedOptions(newT);
                            }
                          }}
                          sx={{
                            textTransform: "none",
                            bgcolor: isSelected ? "#719879" : "transparent",
                            color: isSelected ? "white" : "#719879",
                          }}
                        >
                          {opt.name} - Rs.{opt.price}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenVarModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmVariations}
            sx={{ bgcolor: "#719879" }}
          >
            Add to Cart
          </Button>
        </DialogActions>
      </Dialog>
      {/* Submit Modal with Print Options */}
      <ValidationModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
      >
        <div className="modal__popup">
          <div className="Portal-Login-Form">
            <div className="ValidationModal">
              <span>Choose Submit Option</span>
              <p style={{ fontSize: "14px", color: "#666", marginTop: "10px" }}>
                Would you like to print the receipt or skip printing?
              </p>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                  marginTop: "20px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  className="formSubmitButton"
                  onClick={() => {
                    setIsSubmitModalOpen(false);
                    onSubmit(null);
                  }}
                  style={{
                    backgroundColor: "#4CAF50",
                    padding: "10px 20px",
                    borderRadius: "4px",
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Skip Print
                </button>

                <button
                  type="button"
                  className="formSubmitButton"
                  onClick={() => {
                    setIsSubmitModalOpen(false);
                    // setIsReciptOpen(true)
                    onPrint();
                  }}
                  style={{
                    backgroundColor: "#2196F3",
                    padding: "10px 20px",
                    borderRadius: "4px",
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Print
                </button>

                <button
                  type="button"
                  className="formSubmitButton"
                  onClick={() => setIsSubmitModalOpen(false)}
                  style={{
                    backgroundColor: "#757575",
                    padding: "10px 20px",
                    borderRadius: "4px",
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </ValidationModal>
      <ValidationModal
        isOpen={isSubmittingModalOpen}
        onClose={closeSubmittingModal}
      >
        <div className="modal__popup">
          <div className="Portal-Login-Form">
            <div className="ProgressModal">
              <Box className="progressOfSubmitting" sx={{ width: "100%" }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 15,

                    borderRadius: 10,

                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#719879",
                    },
                  }}
                />
              </Box>

              <span>Submitting Request</span>
            </div>
          </div>
        </div>
      </ValidationModal>
      <RejectionModal isOpen={isReciptOpen} onClose={closeModalRecipt}>
        <div
          className="modal__popup"
          style={{ maxWidth: "500px", width: "100%" }}
        >
          <div className="Portal-Login-Form">
            <div className="ValidationModal" style={{ display: "block" }}>
              {/* --- RECEIPT PREVIEW SECTION --- */}

              <div
                style={{
                  background: "white",

                  padding: "30px",

                  border: "2px solid #333",

                  marginBottom: "20px",

                  fontFamily: "'Arial', sans-serif",

                  maxWidth: "450px",

                  margin: "0 auto",
                }}
              >
                {/* HEADER */}

                <div
                  style={{
                    textAlign: "center",

                    borderBottom: "2px solid #333",

                    paddingBottom: 15,
                  }}
                >
                  <img src="/logo.png" alt="logo" style={{ height: 50 }} />

                  <h1 style={{ fontSize: 20, margin: "8px 0" }}>
                    ELEGANCE MAKEUP STUDIO
                  </h1>

                  <div style={{ fontSize: 11 }}>
                    Bahria Orchard, Lahore
                    <br />
                    +923345555882
                  </div>
                </div>

                <h2 style={{ textAlign: "center", margin: "15px 0" }}>
                  RECEIPT
                </h2>

                {/* CUSTOMER INFO */}

                <div style={{ fontSize: 12, marginBottom: 15 }}>
                  <div
                    style={{
                      display: "flex",

                      justifyContent: "space-between",
                    }}
                  >
                    <span>
                      <strong>Customer:</strong>
                    </span>

                    <span>
                      {/* {customerExist === "existing" ? customerName : fullName} */}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",

                      justifyContent: "space-between",
                    }}
                  >
                    <span>
                      <strong>Date:</strong>
                    </span>

                    <span>{new Date().toLocaleDateString()}</span>
                  </div>

                  <div
                    style={{
                      display: "flex",

                      justifyContent: "space-between",
                    }}
                  >
                    <span>
                      <strong>Cashier:</strong>
                    </span>

                    <span>{cashierName}</span>
                  </div>
                </div>

                {/* TABLE */}

                <table
                  style={{
                    width: "100%",

                    fontSize: 11,

                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: "1px solid #333" }}>
                      <th align="left">SERVICE</th>

                      <th align="center">QTY</th>

                      <th align="right">BASE</th>

                      <th align="right">TAX</th>

                      <th align="right">TOTAL</th>
                    </tr>
                  </thead>

                  <tbody>
                    {/* {normalizedItems.map((s) => {
                      const qty = s.quantity || 1;

                      // 1. Original Tax-Inclusive Total

                      const inclusiveTotal = (s.finalPrice ?? s.price) * qty;

                      // 2. Original Base Price (Tax-exclusive)

                      const originalBase = inclusiveTotal / 1.05;

                      // 3. Discount (Applied to Base)

                      const discountedBase = originalBase;

                      // 4. Tax calculated on the discounted value

                      const tax = discountedBase * 0.05;

                      const lineTotal = discountedBase + tax;

                      let serviceName = s.name;

                      if (s.variations && s.variations.length > 0) {
                        const variationLabels = s.variations

                          .map((v) => `${v.optionName} (${v.typeName})`)

                          .join(", ");

                        serviceName = `${s.name} - ${variationLabels}`;
                      }

                      return (
                        <tr
                          key={s.id}
                          style={{ borderBottom: "1px dotted #ccc" }}
                        >
                          <td>{serviceName}</td>

                          <td align="center">{qty}</td>

                          <td align="right">{originalBase.toFixed(0)}</td>

                          <td align="right">{tax.toFixed(0)}</td>

                          <td align="right" style={{ fontWeight: "bold" }}>
                            {lineTotal.toFixed(0)}
                          </td>
                        </tr>
                      );
                    })} */}
                  </tbody>
                </table>

                {/* TOTALS */}

                <div style={{ marginTop: 15, fontSize: 12 }}>
                  {(() => {
                    let totalOriginalBase = 0;

                    let totalDiscount = 0;

                    let totalTax = 0;

                    let grandTotal = 0;

                    // normalizedItems.forEach((s) => {
                    //   const qty = s.quantity || 1;

                    //   const inclusiveTotal = (s.finalPrice ?? s.price) * qty;

                    //   const originalBase = inclusiveTotal / 1.05;

                    //   const discount = getItemDiscount(s);

                    //   const discountedBase = originalBase - discount;

                    //   const tax = discountedBase * 0.05;

                    //   totalOriginalBase += originalBase;

                    //   totalDiscount += discount;

                    //   totalTax += tax;

                    //   grandTotal += discountedBase + tax;
                    // });

                    return (
                      <>
                        <div
                          style={{
                            display: "flex",

                            justifyContent: "space-between",
                          }}
                        >
                          <span>Sub Total (excl. Tax):</span>

                          <span>{totalOriginalBase.toFixed(0)}</span>
                        </div>

                        {totalDiscount > 0 && (
                          <div
                            style={{
                              display: "flex",

                              justifyContent: "space-between",

                              color: "#d32f2f",
                            }}
                          >
                            <span>Discount:</span>

                            <span>-{totalDiscount.toFixed(0)}</span>
                          </div>
                        )}

                        <div
                          style={{
                            display: "flex",

                            justifyContent: "space-between",
                          }}
                        >
                          <span>Total Tax (8%):</span>

                          <span>{totalTax.toFixed(0)}</span>
                        </div>

                        <div
                          style={{
                            display: "flex",

                            justifyContent: "space-between",

                            fontSize: 16,

                            fontWeight: "bold",

                            borderTop: "2px solid #333",

                            marginTop: 10,

                            paddingTop: 10,
                          }}
                        >
                          <span>GRAND TOTAL:</span>

                          <span>{grandTotal.toFixed(0)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* ACTIONS */}

              <div
                className="CloseRejectionModalButton"
                style={{
                  justifyContent: "center",

                  display: "flex",

                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  className="CloseRejectionModal"
                  onClick={closeModalRecipt}
                >
                  Go Back
                </button>

                <button
                  type="button"
                  className="rejectButtonValidationModal"
                  style={{
                    background: "#719879",

                    cursor: disablePrint ? "progress" : "pointer",

                    padding: "10px 30px",
                  }}
                  disabled={disablePrint}
                  onClick={onPrint}
                >
                  {disablePrint ? "Printing..." : "Confirm & Print"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </RejectionModal>
    </Layout>
  );
};

export default AddBooking;
